import React, { useEffect, useState } from 'react';
import { Typography, Button, Upload, Spin, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconPhotoPlus } from '@tabler/icons-react';
import { IDisputeEvidence } from '../interfaces/heavyDutyMachinery/IBookingDispute';
import { getDisputeEvidenceObjectUrl } from '../services/DisputeService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { pillToneStyle } from '../theme/creamyTokens';

const { Title, Text } = Typography;

interface EvidenceThumbProps {
  bookingId: string;
  evidence: IDisputeEvidence;
  mine: boolean;
}

/**
 * One piece of evidence. Fetched through the authenticated endpoint and
 * held as a local object URL -- same convention as the check-in/out photo
 * thumbnails, for the same reason: this is material somebody may end up
 * arguing over, so it is never reachable from a guessable URL.
 */
const EvidenceThumb: React.FC<EvidenceThumbProps> = ({ bookingId, evidence, mine }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    getDisputeEvidenceObjectUrl(bookingId, evidence.id)
      .then((result) => {
        objectUrl = result;
        if (cancelled) {
          window.URL.revokeObjectURL(result);
          return;
        }
        setUrl(result);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [bookingId, evidence.id]);

  return (
    <div>
      <div
        style={{
          position: 'relative',
          aspectRatio: '1 / 1',
          borderRadius: 12,
          overflow: 'hidden',
          background: token.colorFillQuaternary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {url ? (
          <img src={url} alt={evidence.caption ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : failed ? (
          <Text type="secondary" style={{ fontSize: 11 }}>
            —
          </Text>
        ) : (
          <Spin size="small" />
        )}
        {/* Whose it is matters more than what it shows: the same photo means
            opposite things depending on who took it. */}
        <span
          style={{
            position: 'absolute',
            left: 4,
            top: 4,
            padding: '1px 7px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            ...pillToneStyle(mine ? 'success' : 'default', token),
          }}
        >
          {translate(t, mine ? TRANSLATIONS.DISPUTE_EVIDENCE_MINE : TRANSLATIONS.DISPUTE_EVIDENCE_THEIRS)}
        </span>
      </div>
      {evidence.caption && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
          {evidence.caption}
        </Text>
      )}
    </div>
  );
};

interface DisputeEvidenceGridProps {
  bookingId: string;
  evidence: IDisputeEvidence[];
  userId?: string;
  /** Omitted once the dispute is closed -- there is nothing left to prove. */
  onUpload?: (file: File) => void;
  uploading?: boolean;
}

/**
 * Both sides' evidence, in one grid rather than two.
 *
 * Deliberately not split into "yours" and "theirs" columns: the point of
 * the exercise is that both parties look at the same set of material, and
 * separating it into camps invites reading only your own half. Each item
 * carries whose it is instead.
 */
const DisputeEvidenceGrid: React.FC<DisputeEvidenceGridProps> = ({ bookingId, evidence, userId, onUpload, uploading }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Title level={5} style={{ margin: '0 0 12px' }}>
        {translate(t, TRANSLATIONS.DISPUTE_EVIDENCE_TITLE)}
      </Title>

      {evidence.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
          {translate(t, TRANSLATIONS.DISPUTE_EVIDENCE_EMPTY)}
        </Text>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {evidence.map((item) => (
            <EvidenceThumb key={item.id} bookingId={bookingId} evidence={item} mine={item.uploadedByUserId === userId} />
          ))}
        </div>
      )}

      {onUpload && (
        <Upload
          accept="image/jpeg,image/png"
          showUploadList={false}
          beforeUpload={(file) => {
            onUpload(file as File);
            // The upload goes through apiUploadService so it carries the
            // auth header like everything else -- never antd's own XHR.
            return false;
          }}
        >
          <Button
            shape="round"
            block
            loading={uploading}
            icon={<IconPhotoPlus size={16} />}
            style={{ marginTop: 14, height: 44 }}
          >
            {translate(t, TRANSLATIONS.DISPUTE_EVIDENCE_ADD)}
          </Button>
        </Upload>
      )}
    </div>
  );
};

export default DisputeEvidenceGrid;
